// src/lib/utils/formHoneypot.ts
//
// docs/spam-defence_plan.md Phase 4, task 4.2: every form gets a spam trap by
// default, not only the forms that configure `Form.honeypotField` themselves
// (today just our own site forms, src/config/siteForms.ts). This module is
// the one place that decides which field name is the trap for a given form,
// so validation.ts (the check, this task) and every renderer (task 4.3)
// never disagree about the name.

/** The minimal shape this module needs from a form row. */
export interface HoneypotFormShape {
  honeypotField?: string | null;
}

/**
 * The trap field every form gets when it has not configured its own. Not a
 * common autofill name, so a browser's saved-address autofill never fills it
 * on a real visitor's behalf (CLAUDE.md section 7 / plan Phase 4 Edge Cases).
 */
export const DEFAULT_HONEYPOT_FIELD = "kiroku_hp_field";

/**
 * The field name the server checks for this form: the form's own configured
 * name if it set one, otherwise the default trap every form gets for free.
 */
export function effectiveHoneypotField(
  form: HoneypotFormShape | null | undefined,
): string {
  return form?.honeypotField || DEFAULT_HONEYPOT_FIELD;
}

/** The minimal shape a renderer needs to decide whether a field already exists for the default name. */
export interface HoneypotFieldNameShape {
  name?: string | null;
}

/** `HoneypotFormShape` plus the `fields` a renderer already has in hand. */
export interface HoneypotFormWithFieldsShape extends HoneypotFormShape {
  fields?: HoneypotFieldNameShape[] | null;
}

/**
 * docs/spam-defence_plan.md task 4.3: does this form still need a renderer to
 * inject the synthetic default trap into what it draws?
 *
 * False the moment the owner has configured `honeypotField` themselves: task
 * 4.2's own citation of `KirokuContactFormComponent.astro`'s comment is that a
 * configured honeypot is two halves that must already agree (a builder-added
 * `type: "honeypot"` field carrying that exact name, and the column naming
 * it), so a form that reaches this function with `honeypotField` set has
 * already had its trap rendered by that existing, opt-in path -- adding a
 * second one here would draw two inputs, one of them unchecked.
 *
 * Also false in the inconceivable case a form already declares a field
 * literally named `DEFAULT_HONEYPOT_FIELD` (someone typed the reserved name
 * into the builder by hand), so a renderer that always injects this field
 * when this function returns true can never render two inputs sharing one
 * `name` attribute.
 */
export function needsDefaultHoneypotField(
  form: HoneypotFormWithFieldsShape | null | undefined,
): boolean {
  if (form?.honeypotField) return false;
  const fields = form?.fields;
  if (!Array.isArray(fields)) return true;
  return !fields.some((f) => f?.name === DEFAULT_HONEYPOT_FIELD);
}

/** What a renderer needs to supply to place the synthetic field it injects. */
export interface DefaultHoneypotFieldInit {
  /** The form the field belongs to, so a synthetic row is shaped like a real one. */
  formId: string;
  /**
   * Which step it belongs to on a multi-step form (the first step a visitor
   * reaches, so a naive filler that scans the page for empty inputs meets it
   * immediately). `null`/omitted on a single-step form.
   */
  stepId?: string | null;
  /** Sort position among the fields it is appended to; renderers pass the list's own length so it sorts last. */
  order?: number;
}

/**
 * The synthetic field object a renderer appends to its own field list when
 * `needsDefaultHoneypotField` is true, so the field-driven renderers
 * (`formEmbedUtils.ts`'s HTML/AJAX generators, `kiroku-embed.ts`'s React
 * mode) draw it through the `case "honeypot":` branch each already has, with
 * no new rendering code of their own. `LiveForm.tsx` renders its own copy
 * directly instead of using this (see that file's comment), because its
 * submit handler already reads the honeypot straight off the DOM by name
 * rather than by walking `form.fields`.
 */
export function buildDefaultHoneypotField(init: DefaultHoneypotFieldInit): {
  id: string;
  formId: string;
  stepId: string | null;
  label: string;
  name: string;
  type: "honeypot";
  required: false;
  placeholder: null;
  order: number;
  validation: null;
  options: null;
} {
  return {
    id: "kiroku-default-honeypot",
    formId: init.formId,
    stepId: init.stepId ?? null,
    label: "",
    name: DEFAULT_HONEYPOT_FIELD,
    type: "honeypot",
    required: false,
    placeholder: null,
    order: init.order ?? Number.MAX_SAFE_INTEGER,
    validation: null,
    options: null,
  };
}

/*
 * docs/spam-defence_plan.md Phase 4, task 4.4: a signed render timestamp, so
 * the submit path can log (never score, never reject) whether a form was
 * filled in suspiciously fast. This task's own Files list is the check only
 * (this module, src/pages/api/forms/[id].ts and this module's test) -- no
 * renderer embeds the token in a form yet, matching how task 4.2 shipped the
 * default-honeypot check before task 4.3 wired up the renderers.
 *
 * Signed with Web Crypto (`crypto.subtle`), not Node's `crypto` module as
 * `src/lib/email/unsubscribe.ts` uses. The reason is *not* the server route's
 * own `export const config = { runtime: "edge" }`: `.wolf/cerebrum.md`'s
 * 2026-09-29 entry already found that export is dead documentation under
 * this project's single-function Vercel adapter config (no
 * `functionPerRoute`), and Node's `crypto` module works fine there --
 * confirmed again here by trying it, not just trusting the note: it
 * type-checked and passed every unit test, and only broke
 * `tests/guard/embed-bundle-is-current.test.ts` ("Could not resolve
 * 'crypto'"). The real constraint is the *other* consumer this module
 * already has, task 4.2/4.3's own reason this file exists: `kiroku-embed.ts`
 * imports it too, and that file is bundled separately by
 * `scripts/build-embed.mjs` for the *browser*, no `platform`/`external`
 * override, so a bare `"crypto"` import cannot resolve there at all. Web
 * Crypto is the one HMAC primitive both consumers actually have: Node 19+
 * globally, and every real browser. `crypto.subtle.verify` also does its own
 * constant-time comparison, so no manual timing-safe compare is needed here.
 */

/** The payload key a future renderer will post the signed render timestamp under. */
export const FILL_TIME_TOKEN_FIELD = "kiroku_rt";

/** Below this, filling in the form counts as suspiciously fast (plan Phase 4, task 4.4). */
export const FILL_TIME_TOO_FAST_MS = 3000;

export interface FillTimeObservation {
  tooFast: boolean;
  elapsedMs: number;
}

/**
 * `JWT_SECRET`: the one app-wide secret `src/lib/config/env.ts` already marks
 * required, so this log-only signal needs no env var of its own (CLAUDE.md
 * section 2: "an environment variable is a last resort").
 */
function fillTimeSecret(): string | null {
  return process.env.JWT_SECRET || null;
}

async function hmacKey(secret: string): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(secret),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign", "verify"],
  );
}

function bytesToHex(buffer: ArrayBuffer): string {
  return Array.from(new Uint8Array(buffer))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

/** Null on anything that is not a well-formed hex string of whole bytes. */
function hexToBytes(hex: string): Uint8Array | null {
  if (hex.length === 0 || hex.length % 2 !== 0 || !/^[0-9a-f]+$/i.test(hex)) {
    return null;
  }
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes;
}

/**
 * Mints a signed render-timestamp token. Throws when there is no secret to
 * sign with: unlike verify, this only ever runs at render time (or in a
 * test), never on the hot submit path a missing secret must not be allowed
 * to crash.
 */
export async function issueFillTimeToken(
  renderedAt: number = Date.now(),
): Promise<string> {
  const secret = fillTimeSecret();
  if (!secret) {
    throw new Error("JWT_SECRET is required to sign a fill-time token");
  }
  const signature = bytesToHex(
    await crypto.subtle.sign(
      "HMAC",
      await hmacKey(secret),
      new TextEncoder().encode(String(renderedAt)),
    ),
  );
  return btoa(JSON.stringify({ t: renderedAt, sig: signature }));
}

/**
 * Verifies a fill-time token pulled off the submitted payload. Never throws
 * and never blocks a submission: a missing, malformed, tampered, or unsigned
 * (no `JWT_SECRET`) token all return null, indistinguishable from "this
 * visitor's client predates the token" -- the same way a missing honeypot
 * field reads as "not filled" (task 4.2's own edge case).
 */
export async function verifyFillTimeToken(
  token: unknown,
  now: number = Date.now(),
): Promise<FillTimeObservation | null> {
  if (typeof token !== "string" || !token) return null;
  const secret = fillTimeSecret();
  if (!secret) return null;

  let parsed: { t?: unknown; sig?: unknown };
  try {
    parsed = JSON.parse(atob(token));
  } catch {
    return null;
  }

  const { t, sig } = parsed;
  if (typeof t !== "number" || !Number.isFinite(t) || typeof sig !== "string") {
    return null;
  }
  const signatureBytes = hexToBytes(sig);
  if (!signatureBytes) return null;

  try {
    const valid = await crypto.subtle.verify(
      "HMAC",
      await hmacKey(secret),
      signatureBytes,
      new TextEncoder().encode(String(t)),
    );
    if (!valid) return null;
  } catch {
    // A malformed key or signature buffer throws rather than returning
    // false; either way it is not a valid token.
    return null;
  }

  const elapsedMs = Math.max(0, now - t);
  return { tooFast: elapsedMs < FILL_TIME_TOO_FAST_MS, elapsedMs };
}

/**
 * The three states the submit route (task 2.5,
 * docs/spam-network-and-fill-time_plan.md) needs to tell apart on
 * `observed.tokenState`, which a bare `FillTimeObservation | null` cannot:
 * `null` from `verifyFillTimeToken` already means both "nobody sent one" and
 * "somebody sent a bad one", and those are different facts about the same
 * submission. `"absent"` is the renderer never ran or never finished (a
 * static HTML form, or a token that never arrived) -- not a sign of
 * anything, the same way a missing honeypot value reads as "not filled".
 * `"invalid"` is a token that is present but does not verify: malformed,
 * tampered, or signed before `JWT_SECRET` existed. `"valid"` is everything
 * `verifyFillTimeToken` accepts.
 */
export type FillTimeTokenState = "absent" | "invalid" | "valid";

/** Wraps `verifyFillTimeToken`, which stays the single verifier; this only labels its answer. */
export async function fillTimeTokenState(
  token: unknown,
  now: number = Date.now(),
): Promise<FillTimeTokenState> {
  if (token === undefined || token === null || token === "") return "absent";
  const observation = await verifyFillTimeToken(token, now);
  return observation ? "valid" : "invalid";
}
